import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, ArrowLeft, Coins } from "lucide-react";
import { api, formatApiErrorDetail, newIdempotencyKey } from "../lib/api";
import { BigButton } from "../design/BigButton";
import { SceneCanvas } from "../gameplay/SceneCanvas";
import { useSettings } from "../contexts/SettingsContext";
import { speech } from "../audio/speechService";
import "./daily.css";

/**
 * Daily Find — one word per day, no manipulative streak pressure.
 * Renders a stripped-down gameplay canvas with a single target.
 */
export function DailyFindScreen() {
  const { profileId } = useParams();
  const navigate = useNavigate();
  const settings = useSettings();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ready | found | already
  const [caption, setCaption] = useState("");
  const [foundIds, setFoundIds] = useState(new Set());
  const [tokensAwarded, setTokensAwarded] = useState(0);
  const [balance, setBalance] = useState(0);
  const transformRef = useRef(null);

  async function load() {
    try {
      const { data } = await api.get(`/daily-find/${profileId}`);
      setData(data);
      if (data.completedToday) setStatus("already");
      else if (!data.available) setStatus("empty");
      else setStatus("ready");
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail || e.message));
    }
  }
  useEffect(() => { load(); }, [profileId]);

  useEffect(() => {
    if (status === "ready" && data?.target?.learningObject) {
      const lo = data.target.learningObject;
      const line = `Today's Daily Find is a ${lo.spokenName || lo.displayName}! Can you find it?`;
      setCaption(line);
      speech.speak(line, { speed: settings.narrationSpeed });
    }
  }, [status, data, speech, settings]);

  async function complete() {
    try {
      const { data: r } = await api.post(`/daily-find/${profileId}/complete`, {
        idempotencyKey: newIdempotencyKey(),
      });
      setTokensAwarded(r.tokensAwarded);
      setBalance(r.tokenBalance);
      setStatus("found");
      speech.speak(`Great job! You earned ${r.tokensAwarded} tokens.`, { speed: settings.narrationSpeed });
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail || e.message));
    }
  }

  const target = data?.target;
  const objects = useMemo(() => {
    if (!target) return [];
    return [{
      id: target.levelObject.id,
      xPercent: target.levelObject.xPercent,
      yPercent: target.levelObject.yPercent,
      widthPercent: target.levelObject.widthPercent,
      heightPercent: target.levelObject.heightPercent,
      hitboxPaddingPercent: target.levelObject.hitboxPaddingPercent,
      learningObject: target.learningObject,
    }];
  }, [target]);

  return (
    <div className="wmleye-app daily-screen" data-testid="daily-find">
      <header className="daily-screen__header">
        <BigButton variant="ghost" onClick={() => navigate(`/child/${profileId}/home`)} data-testid="daily-back">
          <ArrowLeft size={20} /> Home
        </BigButton>
        <h1><Sparkles size={22} /> Daily Find</h1>
      </header>

      {err && <div className="daily-screen__notice" data-testid="daily-error">{err}</div>}

      {status === "loading" && <p style={{ textAlign: "center" }}>Getting today's word ready…</p>}

      {status === "empty" && (
        <div className="daily-screen__card" data-testid="daily-empty">
          <img src="/api/uploads/mascot_ollie.png" alt="Ollie the owl" onError={(e) => e.currentTarget.style.display = "none"} />
          <h2>{data?.enabled === false ? "Daily Find is off" : "First play a level!"}</h2>
          <p>{data?.message}</p>
          <BigButton onClick={() => navigate(`/child/${profileId}/worlds`)} data-testid="daily-go-play">
            Play a level
          </BigButton>
        </div>
      )}

      {status === "already" && (
        <div className="daily-screen__card" data-testid="daily-already">
          <h2>Nice work already today!</h2>
          <p>You already found today's Daily Find. Come back tomorrow for a new word.</p>
          <BigButton variant="ghost" onClick={() => setStatus("ready")} data-testid="daily-try-again">
            Try again just for fun
          </BigButton>
          <BigButton onClick={() => navigate(`/child/${profileId}/home`)}>Back home</BigButton>
        </div>
      )}

      {status === "ready" && target && (
        <>
          <div className="daily-screen__prompt" data-testid="daily-prompt">
            <div className="daily-screen__thumb">
              {target.learningObject.thumbnailUrl ? (
                <img src={target.learningObject.thumbnailUrl} alt={target.learningObject.displayName} />
              ) : (
                <span>{target.learningObject.displayName?.[0]}</span>
              )}
            </div>
            <div>
              <h2 data-testid="daily-target-name">Find the {target.learningObject.displayName}!</h2>
              <p style={{ margin: 0 }}>In: <b>{target.level.name}</b></p>
            </div>
          </div>

          <div className="daily-screen__canvas">
            <SceneCanvas
              sceneImageUrl={target.level.sceneImageUrl}
              objects={objects}
              foundIds={foundIds}
              transformRef={transformRef}
              onHit={(o) => {
                if (foundIds.has(o.id)) return;
                setFoundIds(new Set([o.id]));
                setCaption(`Great job! You found the ${target.learningObject.displayName}!`);
                setTimeout(complete, 700);
              }}
              onMiss={() => {
                setCaption("Keep looking — you can do it!");
              }}
            />
          </div>
          <p className="daily-screen__caption" data-testid="daily-caption">{caption}</p>
        </>
      )}

      {status === "found" && (
        <motion.div
          className="daily-screen__card daily-screen__card--celebrate"
          data-testid="daily-celebration"
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
        >
          <h2>You found it! 🎉</h2>
          <p>You earned <b>{tokensAwarded}</b> <Coins size={20} /> tokens.</p>
          <p style={{ color: "#5a4f4a" }}>New balance: {balance}</p>
          <BigButton onClick={() => navigate(`/child/${profileId}/home`)} data-testid="daily-home">Back home</BigButton>
        </motion.div>
      )}
    </div>
  );
}
